import { ViewBooking } from "collins-js-sdk";
import { ChevronDown } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useAppContext } from "../../../../../hooks/useAppContext";
import { Input } from "../../../../ui/input";
import { WebViewPopover } from "../../SubHeader/WebViewPopover";

type EditBookingCustomerType = {
  booking: ViewBooking;
  updateBooking: (bookingProps: Partial<ViewBooking>) => void;
};

const EditBookingCustomer = ({
  booking,
  updateBooking
}: EditBookingCustomerType) => {
  const { venueGroupConfig } = useAppContext();
  const [isSourcePopoverOpen, setIsSourcePopoverOpen] =
    useState<boolean>(false);

  const sourceOptions = useMemo(() => {
    return (
      venueGroupConfig?.booking_sources?.map((source) => ({
        label: source.label,
        value: source.label
      })) ?? []
    ).concat({ label: "", value: "" });
  }, [venueGroupConfig?.booking_sources]);

  const handleSourceChange = (value: string) => {
    setIsSourcePopoverOpen(false);
    if (booking.partnerSource !== value) {
      updateBooking({
        partnerSource: value
      });
    }
  };

  const selectedSourceLabel = useMemo(() => {
    return booking.partnerSource ?? "Select a source";
  }, [booking.partnerSource]);

  return (
    <div>
      <div className="EditCustomerRow flex max-[600px]:flex-col py-panel-padding">
        <div className="flex items-center justify-between w-1/2 max-[600px]:w-full mr-2 max-[600px]:mr-0 h-[37px]">
          <p className="text-left font-bold m-0 pr-2 text-collins-form-label text-form-label">
            First Name
          </p>
          <Input
            className="flex-grow w-full bg-background border border-input rounded-md text-sm font-normal px-3 py-2 h-9 text-right !bg-[hsl(var(--background))] p-2.5 max-w-[15rem] text-left "
            value={booking.firstName ?? ""}
            type="text"
            placeholder="Enter first name"
            onChange={(e) =>
              updateBooking({
                firstName: e.target.value
              })
            }
          />
        </div>
        <div className="EditCustomerFormSection EditCustomerFormSection--second flex items-center justify-between w-1/2 max-[600px]:w-full ml-2 max-[600px]:ml-0 h-[37px]">
          <p className="text-left font-bold m-0 pr-2 text-collins-form-label text-form-label">
            Last Name
          </p>
          <Input
            className="flex-grow w-full bg-background border border-input rounded-md text-sm font-normal px-3 py-2 h-9 text-right !bg-[hsl(var(--background))] p-2.5 max-w-[15rem] text-left"
            value={booking.lastName ?? ""}
            type="text"
            placeholder="Enter last name"
            onChange={(e) =>
              updateBooking({
                lastName: e.target.value
              })
            }
          />
        </div>
      </div>
      <div className="EditCustomerRow flex max-[600px]:flex-col py-panel-padding">
        <div className="EditCustomerFormSection flex items-center justify-between w-1/2 max-[600px]:w-full mr-2 max-[600px]:mr-0 h-[37px]">
          <p className="text-left font-bold m-0 pr-2 text-collins-form-label text-form-label">
            Email
          </p>
          <Input
            className="flex-grow w-full max-w-[25rem] bg-background border border-input rounded-md text-sm font-normal px-3 py-2 h-9 text-right !bg-[hsl(var(--background))] p-2.5  max-w-[15rem] text-left"
            value={booking.email ?? ""}
            type="email"
            placeholder="Enter an email"
            onChange={(e) =>
              updateBooking({
                email: e.target.value
              })
            }
          />
        </div>
        <div className="EditCustomerFormSection EditCustomerFormSection--second flex items-center justify-between w-1/2 max-[600px]:w-full ml-2 max-[600px]:ml-0 h-[37px]">
          <p className="text-left font-bold m-0 pr-2 text-collins-form-label text-form-label">
            Phone
          </p>
          <Input
            className="flex-grow w-full max-w-[25rem] bg-background border border-input rounded-md text-sm font-normal px-3 py-2 h-9 text-right !bg-[hsl(var(--background))] p-2.5 max-w-[15rem] text-left EditCustomerInput--number"
            value={booking.phone ?? ""}
            type="number"
            placeholder="Enter a phone number"
            onChange={(e) =>
              updateBooking({
                phone: e.target.value
              })
            }
          />
        </div>
      </div>
      <div className="EditCustomerRow flex max-[600px]:flex-col py-panel-padding">
        <div className="EditCustomerFormSection flex items-center justify-between w-1/2 max-[600px]:w-full mr-2 max-[600px]:mr-0 h-[37px]">
          <p className="text-left font-bold m-0 pr-2 text-collins-form-label text-form-label">
            Company
          </p>
          <Input
            placeholder="Enter company name"
            className="flex-grow w-full max-w-[25rem] bg-background border border-input rounded-md text-sm font-normal px-3 py-2 h-9 text-right !bg-[hsl(var(--background))] p-2.5 max-w-[15rem] text-left"
            value={booking.company ?? ""}
            type="text"
            onChange={(e) =>
              updateBooking({
                company: e.target.value
              })
            }
          />
        </div>
        <div className="EditCustomerFormSection EditCustomerFormSection--second flex items-center justify-between w-1/2 max-[600px]:w-full ml-2 max-[600px]:ml-0 h-[37px]">
          <p className="text-left font-bold m-0 pr-2 text-collins-form-label text-form-label">
            Source
          </p>
          <WebViewPopover
            open={isSourcePopoverOpen}
            onOpenChange={setIsSourcePopoverOpen}
            side="bottom"
            sideOffset={8}
            content={
              <div className="max-h-[300px] overflow-y-auto px-2 pb-2">
                {sourceOptions.length > 0 ? (
                  sourceOptions.map((source, index) => {
                    const isSelected = source.value === booking.partnerSource;
                    return (
                      <button
                        key={`${source.value}-${index}`}
                        className={`w-full px-3 py-2 text-left text-sm focus:outline-none rounded-md ${
                          isSelected
                            ? "bg-accent text-accent-foreground font-medium"
                            : "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        }`}
                        onClick={() => handleSourceChange(source.value)}
                      >
                        {source.label ?? "(None)"}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    No sources found
                  </div>
                )}
              </div>
            }
          >
            <button className="EditCustomerSourceButton flex-grow flex items-center justify-between w-60 max-w-[25rem] bg-background border !border-[hsl(var(--border))] border-solid rounded-md text-collins-primary font-normal px-3 py-2 h-9 text-right text-primary-color hover:bg-accent hover:text-accent-foreground focus:outline-none">
              <span className="truncate text-left flex-grow">
                {selectedSourceLabel}
              </span>
              <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
            </button>
          </WebViewPopover>
        </div>
      </div>
    </div>
  );
};

export default React.memo<EditBookingCustomerType>(EditBookingCustomer);
